import { createFileRoute, Outlet, redirect, Link, useRouterState } from "@tanstack/react-router";
import { authClient } from "../lib/auth-client";
import { useTheme } from "@/lib/theme";
import {
  CalendarDays,
  Target,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth")({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data?.user) {
      throw redirect({ to: "/login", search: {} });
    }
  },
  component: AuthLayout,
});

const NAV_ITEMS = [
  { to: "/dashboard", label: "Today", icon: CalendarDays },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/review", label: "Review", icon: BarChart3 },
  { to: "/circle", label: "Circle", icon: Users },
];

function AuthLayout() {
  const { mode, setMode } = useTheme();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  function handleSignOut() {
    void authClient.signOut().then(() => window.location.assign("/login"));
  }

  const nextMode = mode === "system" ? "light" : mode === "light" ? "dark" : "system";
  const ThemeIcon = mode === "dark" ? Moon : mode === "light" ? Sun : Monitor;

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex w-56 flex-col shrink-0 border-r border-border bg-sidebar">
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-4 py-5 border-b border-sidebar-border">
          <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-sidebar-primary shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-sidebar-primary" />
          </div>
          <span className="font-semibold text-sm tracking-tight text-sidebar-foreground leading-tight">
            Consistency Circle
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
            const isActive = pathname === to || (to === "/dashboard" && pathname === "/");
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-2 py-3 border-t border-sidebar-border space-y-0.5">
          <Link
            to="/settings"
            className={cn(
              "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
              pathname === "/settings"
                ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
            )}
          >
            <Settings className="w-4 h-4 shrink-0" />
            Settings
          </Link>

          <button
            onClick={() => setMode(nextMode)}
            className="flex w-full items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            <ThemeIcon className="w-4 h-4 shrink-0" />
            {mode === "system" ? "System" : mode === "light" ? "Light" : "Dark"}
          </button>

          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Bottom nav — mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-center border-t border-border bg-sidebar">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
          const isActive = pathname === to || (to === "/dashboard" && pathname === "/");
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                "flex-1 flex flex-col items-center gap-1 py-2.5 text-xs transition-colors",
                isActive
                  ? "text-sidebar-primary font-medium"
                  : "text-sidebar-foreground/60 hover:text-sidebar-foreground"
              )}
            >
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
